/**
 * Owns every character's pixel body: slot allocation, per-frame posing, and
 * the damage path that turns wounds into real matter in the world.
 *
 * Re-rasterisation is gated on *pose change*, not on the frame clock. A body
 * standing still costs nothing; a body running re-rasterises at the rate its
 * gait actually advances. That is what keeps 25 characters affordable.
 */

import { profiler } from '../core/profiler';
import { Flag, M, VOID } from '../sim/materials';
import type { CellWorld } from '../sim/world';
import type { Particles } from '../sim/particles';
import { Comp, World, standingHalfH } from './ecs';
import { DEEP_WATER_SUBMERSION } from './movement';
import {
  BODY_ORIGIN_X_UNITS,
  BODY_ORIGIN_Y_UNITS,
  MAX_BONES,
  RENDER_K,
  Pose,
  bodyQuad,
  charBody,
  collapseSkeleton,
  crouchPose,
  makePixelBody,
  makeSkeleton,
  planFor,
  poseSkeleton,
  rasterize,
  scorchBody,
  shedBody,
  tissueFromComposition,
  woundBody,
  type PixelBody,
  type PoseId,
  type PoseInput,
} from './pixelBody';
import type { DeathRite } from './deathRite';
import { MAX_BODIES } from '../render/pixelBodies';
import { AiState } from './ai/behaviors';
import { Family } from './enemyData';
import { swingPose, type WeaponVisual } from './weaponVisual';

interface Entry {
  body: PixelBody;
  slot: number;
  /** Pose signature at last rasterisation; a change forces a re-raster. */
  sig: number;
  lastX: number;
  attackT: number;
  hurtT: number;
  /** The equipped weapon's appearance, or null when unarmed. */
  visual: WeaponVisual | null;
  /** Length of the attack the swing is animating against, seconds. */
  attackDur: number;
  /**
   * The wind-up inside `attackDur`, seconds. 0 means "not told", which draws
   * the swing exactly as it was drawn before #90 — see `anticipate`.
   */
  attackWindup: number;
  /** Where this body is pointing, or NaN. Set by `noteAim`; see `PoseInput.aim`. */
  aim: number;

  /** Front-hand joint in body-local units, and the resolved swing pose.
   *  Read by the world-space weapon renderer. */
  handX: number;
  handY: number;
  swingAngle: number;
  swingExtend: number;
  swingCurl: number;
  swinging: boolean;
  /**
   * Where the weapon turns, when the style displaced it off the grip (#88).
   * Body-local units — the same units and signs as `handX`/`handY` — resolved
   * here because this is the only place that holds both the pose and the plan
   * whose height `SwingPose.pivotX`/`pivotY` are fractions of.
   *
   * `swingBodyPivot` false leaves these untouched and the renderer uses the
   * hand, which is what all seven hand-pivoted styles do.
   */
  swingBodyPivot: boolean;
  swingPivotX: number;
  swingPivotY: number;

  /** The rite this body is being disposed of by, or null. See `Dying`. */
  dying: Dying | null;
}

/**
 * A body in the middle of its death rite.
 *
 * Everything here is advanced on the **simulation** tick by `stepDeath` and
 * only *read* by `poseOne` on the render tick. That split is not decoration:
 * `poseOne` runs once per rendered frame, so a shed budget spent there would
 * put more of the player's body into the world on a fast monitor than on a slow
 * one, and the amount of matter in the world is simulation state.
 */
interface Dying {
  readonly rite: DeathRite;
  /** Ticks elapsed. Counts to `rite.ticks` and then stops. */
  t: number;
  /**
   * Pixels to shed per shedding tick, or `-1` before the shed starts.
   *
   * **Derived from the body that is actually on screen**, once, on the first
   * shedding tick: `body.total * shedFraction / shedTicks`. Not from
   * `BODY_CELLS`, which is the raster box's 3,944 and roughly thirty times what
   * the player occupies — #42's lesson about deriving a quota from a cap rather
   * than from what is baked, which cost nine tenths of the frame slack for a
   * shape that did not exist.
   *
   * And derived *then* rather than at `beginDeath`, because the body it is a
   * fraction of is the **collapsed** one. A fold does not preserve pixel count:
   * discs that were spread along a standing figure overlap once it is folded,
   * and `sunder` takes the duelist from 128 lit pixels to about 54. A budget
   * derived from the standing figure would ask a 54-pixel heap for 70 pixels
   * and erase it inside a third of the rite.
   *
   * **Fractional, and paid out through `debt`.** The first version rounded up
   * to a whole pixel a tick, which on a body this small is not a rounding
   * error but the entire rate: `slack` asks for 14% of 54 pixels over 40 ticks,
   * i.e. 0.19 a tick, and a floor of one shed 40 and left the duelist with 16
   * pixels. A blow is supposed to leave a corpse.
   */
  perTick: number;
  /** Fractional pixels carried between ticks, so a rate below 1 is a rate. */
  debt: number;
  /** Current fold, 1 → `rite.fold`. Read by `poseOne`. */
  fold: number;
  /** Current widen, 1 → `rite.widen`. The cap is applied at raster time. */
  widen: number;
}

// ------------------------------------------------------- re-raster signature

/**
 * Field widths for `poseSignature`, as *ranges* rather than as bit counts.
 *
 * The signature is a mixed-radix pack — `sig = sig * range + value` per field —
 * which is a bitfield in every sense that matters here (each field occupies its
 * own digits and no field's value can reach another's) without rounding each
 * one up to a power of two. It has to be: the fields need 38 bits, JavaScript's
 * `<<`/`^` are 32-bit, and the old signature's fields overlapped for exactly
 * that reason. A double carries 53 bits of integer exactly, and the product of
 * every range below is 3.3e10, so the pack is lossless.
 *
 * The old signature XORed shifted fields into 32 bits and aliased 42,024 times
 * across a 117,600-state grid. Two failures, not one: the fields overlapped
 * (`(scaleY * 16) | 0` reached bit 25, one past the pose field's bit 24), and
 * the signed terms were packed unbiased, so a leftward or rising body
 * contributed a negative value whose sign bits smeared across every field above
 * it. `scaleY = 0.6, attackT = 0.26` and `scaleY = 1.6, attackT = 0` both
 * hashed to 59865088 with visibly different rasters.
 */
const SIG_POSE = 7; // Pose.IDLE..Pose.SWIM (#169B added SWIM, the 7th)
const SIG_FACING = 2;
const SIG_SWING = 2;
const SIG_SPEED = 33; // floor(speed01 * 16), -16..16, biased by +16
const SIG_VY = 33; // floor(vy01 * 16), -16..16, biased by +16
const SIG_ATTACK = 33; // floor(attack01 * 32), 0..32
const SIG_SQUASH = 64; // floor(squash * 32); shipping squash is 0.78..1.24
const SIG_PHASE = 256; // floor(phase * 24), wrapped every 10.6 rad
/**
 * `crouch`: two states, because the crouch is two states (#98).
 *
 * The field the note under `poseSignature` said would never be needed, and the
 * reason it is needed now is worth stating precisely, because it is *not* that
 * `halfH` became variable. It did — a crouch lowers the collider — but the
 * raster is scaled by `standingHalfH`, which is still written once and never
 * again, so the invariant that comment records still holds and this is not a
 * `halfH` field in disguise. What is new is a term the raster depends on that
 * no other field can imply: `pose` cannot, because a crouch composes with
 * every pose there is (`PoseInput.crouch`), and `squashY` cannot, because it
 * scales bone lengths where the fold scales resolved joints.
 *
 * Two rather than a quantised amount, and this is where the cost of getting it
 * wrong lives. The fold moves the topmost joint by `(1 - fold) * plan.height *
 * scale`, i.e. by up to `2 * halfH` world units, so the half-pixel rule the
 * quanta above are derived from needs `1/(4 * halfH)` — 1/100 on the 25-unit
 * tower, so 128 buckets. The pack has x49 of headroom against 2^53 and 130
 * buckets would spend x130 of it: the signature would start aliasing, which is
 * the one failure this whole structure exists to make impossible. A crouch of
 * one fixed depth costs a factor of two and needs no quantum at all — and it is
 * also what lets `movement.ts` size the collider from a single constant.
 */
const SIG_CROUCH = 2;
/**
 * `aim`: 0 for "not aiming", else 1 + floor(aim * 32) biased over [-PI, PI].
 *
 * Quantised on the same rule as every other field — one quantum must move no
 * joint by more than half a raster pixel on the largest body that ships — not
 * enumerated. `aimAngleFor` yields five values today (NaN and ±PI/4, ±PI/2),
 * and a five-valued field would encode *that function's current shape* rather
 * than the parameter's, so a sixth direction would alias silently into an
 * existing bucket and freeze the arm. 1/32 rad against the tower's 17px arm
 * lever at x1.67 is 0.53px of tip travel.
 *
 * The state space goes from 9.04e11 to 1.85e14 against the 2^53 = 9.01e15 a
 * double carries exactly: x49 of headroom left, down from x9,959. `SIG_CROUCH`
 * then doubles it to 3.71e14 — x24 of headroom — and that number is the reason
 * the crouch is one bit rather than a quantised depth.
 */
const SIG_AIM_MUL = 32;
const SIG_AIM_BIAS = 101; // ceil(PI * 32)
const SIG_AIM = 2 * SIG_AIM_BIAS + 2;

/**
 * How fine each quantum is.
 *
 * Derived, not chosen: **one quantum must move no joint by more than half a
 * raster pixel on the largest body in the roster.** Coarser than that and two
 * states inside one bucket rasterise differently, which is precisely the
 * staleness the gate is supposed to make impossible — a signature that is
 * injective in its packing but lossy in its quantisation is no better than one
 * that collides in its bits.
 *
 * Worked for the tower at x1.67, the largest scale that ships:
 *
 *   speed01  torso lean 0.16 rad over a 27px lever  -> quantum <= 0.116, use 1/16
 *   vy01     leg tuck   0.45 rad over a 15px lever  -> quantum <= 0.074, use 1/16
 *   attack01 lead arm   3.00 rad over a 17px lever  -> quantum <= 0.010, use 1/32
 *   phase    leg swing  0.75 rad over a 15px lever  -> quantum <= 0.044, use 1/24
 *
 * Finer quanta are close to free, which is the part that is easy to get wrong
 * by intuition. The gate cannot fire more than once per frame per body, so
 * refining it cannot add re-rasters to a *moving* body — a running body already
 * re-rasters every frame. All it removes is the skipping of bodies that are
 * genuinely still, and a still body's inputs are exactly constant (gait phase
 * advances by distance travelled, so standing still does not advance it), so it
 * still costs nothing at any resolution.
 */
const SIG_SPEED_MUL = 16;
const SIG_VY_MUL = 16;
const SIG_ATTACK_MUL = 32;
const SIG_SQUASH_MUL = 32;
/**
 * Exported so `tests/headBob.test.ts` can hold the half-pixel rule against the
 * *shipping* quantum rather than against a copy of it. The head bob (#86) is a
 * function of `phase` alone and rides entirely on this field's resolution, so a
 * test that restated `24` would go on passing if this number were coarsened —
 * which is the failure mode the whole comment above is about.
 */
export const SIG_PHASE_MUL = 24;

/**
 * Quantise to the pixel grid, then bias into a non-negative range.
 *
 * `Math.floor`, deliberately, not `| 0`. Truncation toward zero makes the
 * bucket straddling zero twice as wide as every other, so `vx = -16` and
 * `vx = +16` — a body leaning visibly left and visibly right — landed in the
 * same bucket and were assigned the same signature. `floor` gives uniform
 * buckets, which is the property the whole gate depends on: two states in one
 * bucket must produce one image.
 */
function sigField(v: number, mul: number, bias: number, range: number): number {
  const q = Math.floor(v * mul) + bias;
  return q < 0 ? 0 : q >= range ? range - 1 : q;
}

/**
 * The state of a body, as far as its *image* is concerned.
 *
 * `poseOne` skips rasterisation when this is unchanged, which is a pure win
 * only while the map from signature to image is a function: two states that
 * rasterise differently must not share a signature, or a character freezes
 * mid-swing holding a stale raster and nothing ever recomputes it.
 *
 * Everything the raster depends on is here. `squashX` was not, and it scales
 * every bone radius. `halfH` is legitimately absent: the number the raster is
 * scaled by is `standingHalfH`, which is written once at spawn and never again,
 * so it cannot vary within one entry's life, and the signature only ever
 * separates states of the same entry. A crouch lowers `halfH` itself, and it is
 * `crouch` below rather than `halfH` that separates the two rasters — see
 * `SIG_CROUCH`, and see `bodyQuad`'s caller in `poseOne` for why the *scale* is
 * deliberately not the thing a crouch changes.
 *
 * `phase` wraps at 256 quanta (10.6 rad, 1.7 gait cycles). A wrap cannot cause
 * a stale raster — the gate compares against the immediately preceding frame,
 * and gait phase advances by distance travelled, so no single frame covers 21
 * radians. It is a documented limit rather than a hidden one because `breathe`
 * runs at 0.35x and is not commensurate with the stride, so no finite field is
 * exactly periodic.
 */
function poseSignature(p: PoseInput, phase: number, swinging: boolean): number {
  let s = p.pose;
  s = s * SIG_FACING + (p.facing > 0 ? 1 : 0);
  s = s * SIG_SWING + (swinging ? 1 : 0);
  s = s * SIG_SPEED + sigField(p.speed01, SIG_SPEED_MUL, SIG_SPEED_MUL, SIG_SPEED);
  s = s * SIG_VY + sigField(p.vy01, SIG_VY_MUL, SIG_VY_MUL, SIG_VY);
  s = s * SIG_ATTACK + sigField(p.attack01, SIG_ATTACK_MUL, 0, SIG_ATTACK);
  s = s * SIG_SQUASH + sigField(p.squashX, SIG_SQUASH_MUL, 0, SIG_SQUASH);
  s = s * SIG_SQUASH + sigField(p.squashY, SIG_SQUASH_MUL, 0, SIG_SQUASH);
  s = s * SIG_PHASE + sigField(phase % (SIG_PHASE / SIG_PHASE_MUL), SIG_PHASE_MUL, 0, SIG_PHASE);
  s = s * SIG_CROUCH + (p.crouch ? 1 : 0);
  // Bucket 0 is reserved for "not aiming" so that neutral cannot collide with
  // any real angle. `sigField` would map NaN to 0 anyway — `Math.floor(NaN)` is
  // NaN and `NaN < 0` is false, so it would fall through the clamp to `NaN` and
  // poison the whole pack — which is exactly the class of silent arithmetic the
  // note above this function is about.
  s = s * SIG_AIM +
    (p.aim === p.aim ? 1 + sigField(p.aim, SIG_AIM_MUL, SIG_AIM_BIAS, SIG_AIM - 1) : 0);
  return s;
}

const scratchPose: PoseInput = {
  pose: Pose.IDLE,
  phase: 0,
  speed01: 0,
  vy01: 0,
  facing: 1,
  attack01: 0,
  aim: NaN,
  crouch: false,
  squashX: 1,
  squashY: 1,
  weaponLen: 0,
  weaponR: 1,
  weaponMat: 0,
  weaponAngle: 0,
  weaponCurl: 0,
  weaponSegments: 1,
};

// --------------------------------------------------------- anticipation (#90)
//
// SotN's Hellfire holds four of its fourteen frames within 5% of its start
// width before anything travels, and its standing attack peaks at frame 2 of 11
// and spends the other nine settling (docs/22 §4). We had neither shape: the
// pose ran on one linear ramp across the whole node.
//
// The measurement that decided what to do about it is in `tests/anticipation`
// §1, and it moved the change. #90 asked whether our classes are *long* enough
// to hold in — melee nodes are 50–667 ms — and the answer is that length was
// never the constraint. Every node already reserves a wind-up, 10.5% to 32.8%
// of itself, during which `wielder.ts` guarantees no hitbox exists. The frames
// were always there. Nobody drew them: at `attack01 = 1 - attackT / attackDur`
// the lead arm had already swept 18° to 57° of its 172° arc by the time the
// blow became live, so the swing visually committed *before* it could hit.
//
// So this is a pose change and not a timing change. Not one millisecond of
// `nodeTiming` moves. What moves is the *progress the pose is drawn at*, and it
// is driven by the node's own wind-up — there is no per-class constant here and
// none is authored anywhere, because the catalogue already encodes which
// weapons are ponderous. It also does not vary `Pose.ATTACK` per weapon, which
// is ruled out: one arm arc still, read at a different rate, the same way
// `attackDur` already varies the rate and the shared curve does not care.

/**
 * Wind-up below this many ms is drawn exactly as before.
 *
 * One frame at 60 Hz. A hold nobody can render is not a hold, and ten of the
 * catalogue's 119 melee nodes sit here — all of them dashes, airs and second
 * lights, never a heavy.
 */
export const HOLD_RAMP_LO_MS = 1000 / 60;
/** Wind-up at or above this many ms holds at full strength. Three 60 Hz frames. */
export const HOLD_RAMP_HI_MS = 48;
/**
 * How much of the wind-up's progress still leaks through at full strength.
 *
 * Not zero. A pose frozen *exactly* is a dropped frame to the eye; a pose that
 * creeps is gathering. 0.18 puts the arm within 11° of its start at the moment
 * the hitbox opens, against 18–57° before, which is the "few degrees" #90 asked
 * for stated as the number the renderer produces.
 */
export const HOLD_LEAK = 0.18;

/**
 * Re-parameterise swing progress so the wind-up is *drawn* as one.
 *
 * `t` is linear progress through the node, 0 at commitment and 1 at the end of
 * recovery. The result is the progress the arm and the weapon are both posed
 * at — both, from one call, because animating them on two clocks is the exact
 * defect `4e0aad6` fixed and `tests/anticipation` §3 holds shut.
 *
 * Two straight segments joined at the wind-up, where `wf` is the wind-up as a
 * fraction of the node and `k` is how much of the old rate survives inside it:
 *
 *   t < wf   ->  t·k                        gathering, at `k` of the old rate
 *   t >= wf  ->  a line from (wf, wf·k) to (1, 1)   the swing, faster to make up
 *
 * so `p(0) = 0`, `p(1) = 1` exactly, and `p` is monotone for any `k` in (0,1].
 * The node's total length is untouched: the swing still ends when the attack
 * does. `k = 1` is the identity and is returned unchanged rather than computed,
 * so a caller that supplies no wind-up gets bit-identical output.
 */
export function anticipate(t: number, windupSec: number, durSec: number): number {
  if (!(windupSec > 0) || !(durSec > 0)) return t;
  const wf = windupSec / durSec;
  if (!(wf > 0) || wf >= 1) return t;
  const ms = windupSec * 1000;
  if (ms <= HOLD_RAMP_LO_MS) return t;
  const span = HOLD_RAMP_HI_MS - HOLD_RAMP_LO_MS;
  const s = ms >= HOLD_RAMP_HI_MS ? 1 : (ms - HOLD_RAMP_LO_MS) / span;
  const k = 1 - s * (1 - HOLD_LEAK);
  if (t < wf) return t * k;
  const p0 = wf * k;
  // Written from the *far* end — `1 - (1-t)·…` rather than `p0 + (t-wf)·…` —
  // because the two are equal in real arithmetic and not in floating point.
  // The additive form returns 1.0000000000000002 at `t = 1` for nine of the
  // catalogue's 119 nodes, and `attack01` leaving [0,1] is exactly what
  // `tests/pixelBody` "posed swing angles stay inside their declared domain"
  // exists to catch. This form is exact at both ends: `t = 1` gives 1 and
  // `t = wf` gives `p0`.
  return 1 - ((1 - t) * (1 - p0)) / (1 - wf);
}

/** Where the wind-up ends, as a fraction of the announced attack length. */
const TELL_WINDUP = 0.6;
/** How bright the tell is on the frame the enemy commits. */
const TELL_FLOOR = 0.35;

/** Colour multiplier and emissive addition for one body this frame. */
export interface BodyTell {
  r: number;
  g: number;
  b: number;
  glow: number;
}

export class PixelBodySystem {
  private entries = new Map<number, Entry>();
  private freeSlots: number[] = [];
  private skeleton = makeSkeleton(MAX_BONES);
  private readonly tell: BodyTell = { r: 1, g: 1, b: 1, glow: 0 };
  /**
   * Entity indices with a live rite, so `stepDeath` costs literally nothing on
   * the 99.99% of ticks where nobody is dying.
   *
   * A list and not a scan of `entries`, because `for (const [i, e] of map)`
   * allocates an iterator per call and `stepDeath` is on the simulation tick.
   * An empty array's `length` is the whole of the common path.
   */
  private dyingIdx: number[] = [];

  constructor() {
    for (let i = MAX_BODIES - 1; i >= 0; i--) this.freeSlots.push(i);
  }

  /** Attach a body to an entity, built from its composition. */
  attach(w: World, i: number, planName: string): Entry | null {
    const slot = this.freeSlots.pop();
    if (slot === undefined) return null; // atlas full; falls back to a rect
    const plan = planFor(planName);
    const tissue = tissueFromComposition(w.compMat, w.compFrac, i * 4, w.coreMat[i]);
    const entry: Entry = {
      body: makePixelBody(plan, tissue),
      slot,
      sig: -1,
      lastX: w.x[i],
      attackT: 0,
      hurtT: 0,
      visual: null,
      attackDur: 0.26,
      attackWindup: 0,
      aim: NaN,
      handX: 0,
      handY: 0,
      swingAngle: 0,
      swingExtend: 0,
      swingCurl: 0,
      swinging: false,
      swingBodyPivot: false,
      swingPivotX: 0,
      swingPivotY: 0,
      dying: null,
    };
    this.entries.set(i, entry);
    return entry;
  }

  detach(i: number): void {
    const e = this.entries.get(i);
    if (!e) return;
    this.freeSlots.push(e.slot);
    this.entries.delete(i);
    const k = this.dyingIdx.indexOf(i);
    if (k >= 0) this.dyingIdx.splice(k, 1);
  }

  get(i: number): Entry | undefined {
    return this.entries.get(i);
  }

  has(i: number): boolean {
    return this.entries.has(i);
  }

  /**
   * Signal an attack. `durationSec` should be the node's real length so the
   * swing animation finishes exactly when the attack does — a weapon that
   * lingers after the hitbox is gone teaches the wrong timing.
   *
   * `windupSec` is the part of that length during which the hitbox does not yet
   * exist, and supplying it is what buys the anticipation hold (#90). It is
   * optional and defaults to 0, which is the identity remap: a caller that does
   * not know its wind-up — every enemy, via `behaviors.ts`, which announces only
   * `windup + active` — draws exactly the swing it drew before.
   */
  noteAttack(i: number, durationSec = 0.26, windupSec = 0): void {
    const e = this.entries.get(i);
    if (!e) return;
    e.attackDur = Math.max(0.08, durationSec);
    // Against the *clamped* duration, so the fraction the pose is remapped by is
    // the one the pose actually runs on. A 40 ms node announced with a 12 ms
    // wind-up is drawn over 80 ms, and 12/80 is what the arm sees.
    e.attackWindup = Math.max(0, Math.min(windupSec, e.attackDur));
    e.attackT = e.attackDur;
    e.body.dirty = true;
  }

  /**
   * Point this body somewhere. `NaN` clears it, which is the resting state.
   *
   * A setter rather than a field read off the `World`, for the same reason
   * `noteAttack` and `setWeapon` are setters: aim is owned by
   * `WielderState`, one entity in the game has one, and copying it into an
   * ECS column would create a second place for it to be true. Bodies that are
   * never told simply never aim, which is every enemy.
   */
  noteAim(i: number, aim: number): void {
    const e = this.entries.get(i);
    if (!e) return;
    e.aim = aim;
  }

  /** Set (or clear) the equipped weapon's appearance. */
  setWeapon(i: number, visual: WeaponVisual | null): void {
    const e = this.entries.get(i);
    if (!e) return;
    if (e.visual !== visual) e.body.dirty = true;
    e.visual = visual;
  }

  noteHurt(i: number): void {
    const e = this.entries.get(i);
    if (e) e.hurtT = 0.18;
  }

  /**
   * Begin the death rite for this body. Returns whether it started.
   *
   * **It refuses the second call, and that refusal is the point.** docs/23 §7.5
   * decided that the player entity survives its own death as an inert body: it
   * keeps `alive`, it keeps its pixels, and the simulation keeps running under
   * the death panel — so the lava that killed it goes on damaging it, and
   * `Game.damageEntity` goes on being called every tick with hp already at or
   * below zero. `Game.onPlayerDeath` has a run-level guard for the banking side
   * of that (`if (this.run.outcome !== null) return`), and this is the same
   * guard for the visual side. Without it every tick would reset `t` to zero
   * and re-derive `perTick` against a body that has already shed half of
   * itself, and the corpse would sit at the first frame of its own death for as
   * long as the lava lasted.
   *
   * The two guards are deliberately not one. This one is a fact about a *body*
   * and it is true for an enemy that has no run to end; the other is a fact
   * about a run.
   */
  beginDeath(i: number, rite: DeathRite): boolean {
    const e = this.entries.get(i);
    if (!e || e.dying !== null) return false;
    // `perTick: -1` is "not derived yet" and not "zero a tick": the rate is
    // fractional and a legitimate one can round to nothing, so a zero sentinel
    // would re-derive the quota against a body that is already shedding.
    e.dying = { rite, t: 0, perTick: -1, debt: 0, fold: 1, widen: 1 };
    // The tissue transform is a one-off at the start, not a per-tick walk: the
    // relations are idempotent in the direction that matters (ASH ashes into
    // nothing) but `applyEnvironment` is still running over this corpse every
    // 30 ticks, and doing it here means the charred palette is on screen for
    // the frame the player dies rather than up to half a second later.
    if (rite.residue !== null) charBody(e.body, rite.residue);
    e.body.dirty = true;
    this.dyingIdx.push(i);
    return true;
  }

  /** Is this body mid-rite? Used by the tests and by nothing in the game. */
  dyingRite(i: number): DeathRite | null {
    return this.entries.get(i)?.dying?.rite ?? null;
  }

  /**
   * Advance every live death rite by one simulation tick.
   *
   * Called from `Game.stepSimulation`, immediately before `particles.step`, so
   * matter shed this tick starts falling on the tick it left the body.
   *
   * The work this can do in one tick is bounded by construction and the bound
   * is a real number rather than a cap: `perTick` pixels per dying body, where
   * `perTick` was derived at `beginDeath` from that body's own baked pixel
   * count. The worst case in the shipping roster is measured in
   * `tests/deathRite.test.ts`.
   */
  stepDeath(w: World, particles: Particles): void {
    const list = this.dyingIdx;
    if (list.length === 0) return;
    // Compacted in place rather than spliced: a finished rite leaves the list
    // on the tick it finishes, so a corpse the player is reading a stat sheet
    // over costs nothing at all. `e.dying` deliberately stays set — the body
    // has to go on being *posed* as a corpse forever — so "is it dead" and "is
    // there anything left to advance" are two different questions and this is
    // the second one.
    // No `if (d.t >= d.rite.ticks) continue` guard, deliberately: the
    // compaction below is what ends a rite, so an index in this list always has
    // ticks left and the guard could never fire. It was here, it was dead, and
    // the mutation that proved it was deleting it and watching nothing go red.
    let keep = 0;
    for (let k = 0; k < list.length; k++) {
      const i = list[k];
      const e = this.entries.get(i);
      const d = e?.dying;
      if (!e || !d) continue;
      d.t++;
      this.stepRite(w, i, e, d, particles);
      if (d.t < d.rite.ticks) list[keep++] = i;
    }
    list.length = keep;
  }

  /** Bodies with a rite still running. Zero on almost every tick ever played. */
  get dyingCount(): number {
    return this.dyingIdx.length;
  }

  private stepRite(w: World, i: number, e: Entry, d: Dying, particles: Particles): void {
    const r = d.rite;

    // ---- collapse. Ease-out: a body drops fast and settles, it does not
    // approach the floor linearly.
    const u = Math.min(1, d.t / Math.max(1, r.collapseTicks));
    const ease = 1 - (1 - u) * (1 - u);
    const fold = 1 - (1 - r.fold) * ease;
    const widen = 1 + (r.widen - 1) * ease;
    if (fold !== d.fold || widen !== d.widen) {
      d.fold = fold;
      d.widen = widen;
      e.body.dirty = true;
    }

    // ---- shed.
    const since = d.t - r.shedFrom;
    if (since <= 0 || since > r.shedTicks) return;

    const body = e.body;
    // Derived here, on the first shedding tick, against the collapsed body —
    // see `Dying.perTick`.
    if (d.perTick < 0) {
      d.perTick = (body.total * r.shedFraction) / Math.max(1, r.shedTicks);
    }
    // Paid out through `debt`, so a rate below one pixel a tick is still a
    // rate. `Math.ceil` here instead — which is what this was — is not a
    // rounding error on a 54-pixel corpse but the entire quantity: `slack`
    // asks for 0.19 px/tick and a ceiling sheds 40, which is the difference
    // between a body with a wound and no body at all.
    d.debt += d.perTick;
    // Settled in full on the last shedding tick, rather than floored again.
    //
    // `shedTicks * (total / shedTicks)` is `total` in arithmetic and is
    // `total - 1e-14` in doubles, so a rite that asked for the whole body
    // floored its way to one pixel short of it and `rime` left a single lit
    // cell hanging in the air where a corpse had been. Rounding `perTick` up
    // instead would overshoot every intermediate tick; paying the remainder
    // once, at the end, is the same total and the same rate.
    const budget = since === r.shedTicks ? Math.ceil(d.debt) : Math.floor(d.debt);
    if (budget <= 0) return;
    d.debt -= budget;
    // Body-local -> world, the same translation `wound` makes and for the same
    // reason: the box's origin is the body's centre line at its feet, in
    // world units, and a raster pixel is `1 / RENDER_K` world units wide.
    const originX = w.x[i] - BODY_ORIGIN_X_UNITS;
    const originY = w.y[i] + w.halfH[i] - BODY_ORIGIN_Y_UNITS;
    // The burst's centre is the body's own bounds, not the box's: a small
    // figure drawn in a 68x58 box would otherwise blow apart around a point
    // well below and beside itself. Raster-texel space throughout — `dx`/`dy`
    // are directions, not distances, so `RENDER_K` cancels out of them and
    // does not need to appear here.
    const cx = (body.minX + body.maxX) * 0.5;
    const cy = (body.minY + body.maxY) * 0.5;

    shedBody(body, r.order, budget, d.t, (px, py, mat) => {
      let dx: number;
      let dy: number;
      if (r.order === 'burst') {
        dx = px - cx;
        dy = py - cy;
        // A pixel exactly at the centre has no outward direction; give it up.
        if (dx === 0 && dy === 0) dy = -1;
      } else {
        // A mild outward lean, so a body erodes into two sprays rather than one
        // column. `Math.sign` of the offset and nothing finer — the cone does
        // the rest.
        dx = px < cx ? -0.35 : 0.35;
        dy = r.lift;
      }
      particles.emit(
        originX + (px + 0.5) / RENDER_K,
        originY + (py + 0.5) / RENDER_K,
        mat,
        1,
        r.speed,
        r.cone,
        dx,
        dy,
        M.spawnTemp[mat] + r.tempBias,
      );
    });
  }

  /**
   * Advance and re-pose every body. Called once per rendered frame — posing is
   * presentation, and running it on the sim clock would make animation rate
   * depend on simulation load.
   */
  update(w: World, dt: number): void {
    profiler.begin('render');
    for (const [i, e] of this.entries) {
      if (w.alive[i] !== 1) {
        this.detach(i);
        continue;
      }
      this.poseOne(w, i, e, dt);
    }
    profiler.end('render');
  }

  private poseOne(w: World, i: number, e: Entry, dt: number): void {
    const vx = w.vx[i];
    const vy = w.vy[i];
    const grounded = w.grounded[i] === 1;

    e.attackT = Math.max(0, e.attackT - dt);
    e.hurtT = Math.max(0, e.hurtT - dt);

    // Gait phase advances with distance travelled, not with time. Legs that
    // cycle on the clock keep striding when you stop — the classic skate.
    //
    // A dying body's phase is frozen. It is still moving — a corpse sinks,
    // burns and washes downstream like anything else (docs/23 §7.5) — and a
    // gait driven by distance travelled would have it *walking* while it did.
    const dx = Math.abs(w.x[i] - e.lastX);
    e.lastX = w.x[i];
    if (e.dying !== null) {
      // phase held
    } else if (grounded) e.body.phase += dx * 0.42;
    else e.body.phase += dt * 3.2;

    let pose: PoseId;
    // `Pose.DEAD` has existed in `pixelBody.ts` since the poses were written —
    // it drops every bone to `DOWN ± 0.4`, which is a figure with nothing held
    // up — and until this line **nothing ever selected it**. `gait.test.ts`
    // says so in as many words, excluding it from the feet-on-the-ground sweep
    // "because `PixelBodySystem.poseOne` never selects it: a body is detached
    // the frame it dies". Bodies are no longer detached the frame they die, and
    // the branch that was written for this is the branch that runs.
    //
    // `fold === 1` is a rite that does **not** collapse, and the skeletal drop
    // is the other half of the same statement — so `rime` keeps standing in the
    // pose it died in rather than dropping its limbs and then claiming not to
    // have slumped. `IDLE` and not the live pose selection, because the gait
    // phase is frozen below: a corpse washed sideways would otherwise be held
    // in a single frame of `RUN`, which is a statue mid-stride.
    if (e.dying !== null) pose = e.dying.rite.fold < 1 ? Pose.DEAD : Pose.IDLE;
    else if (e.hurtT > 0) pose = Pose.HURT;
    else if (e.attackT > 0) pose = Pose.ATTACK;
    // Same threshold `stepMover` reads to pick `MoveState.SWIM` (#169B) — a
    // body that is physically swimming is drawn swimming, at the exact depth
    // the physics already calls it, above `!grounded`'s own check the way
    // `deepWater` sits above `grounded` in `stepMover`'s own state selection.
    else if (w.submersion[i] > DEEP_WATER_SUBMERSION) pose = Pose.SWIM;
    else if (!grounded) pose = Pose.AIR;
    else if (Math.abs(vx) > 12) pose = Pose.RUN;
    else pose = Pose.IDLE;

    scratchPose.pose = pose;
    scratchPose.phase = e.body.phase;
    // A corpse does not lean into its own drift.
    //
    // Not only a look: `PixelBody.intact` is indexed in raster space, so once
    // the rite starts shedding, any pose term that still moves would slide the
    // silhouette under a mask that stays put and erase whatever arrived at a
    // hole. The corpse is carried by the current, sinks, and is dropped when
    // the ledge under it burns away, so `vx` and `vy` are the terms that would
    // do it.
    //
    // **And it is `rime` that needs this, not the collapsing rites.**
    // `speed01` and `vy01` reach only *angles* in `poseSkeleton` — the torso,
    // neck and head lean, and the `Pose.AIR` leg tuck — and `Pose.DEAD`
    // overwrites every angle with `DOWN ± 0.4` on the line after the switch.
    // So for the four rites that collapse, zeroing these changes nothing that
    // can be drawn. `rime` does not collapse, and `poseOne` therefore poses it
    // `IDLE`, where the lean is live: a frozen corpse washed sideways would
    // lean into the current for the 0.6 s it stands there and then shatter out
    // of a posture it had no business taking. `tests/deathRite.test.ts` runs
    // the drift comparison on `rime` for exactly that reason — on `slack` it
    // is green with these guards deleted, measured.
    const dead = e.dying !== null;
    scratchPose.speed01 = dead ? 0 : Math.max(-1, Math.min(1, vx / 160));
    scratchPose.vy01 = dead ? 0 : Math.max(-1, Math.min(1, vy / 400));
    scratchPose.facing = w.facing[i] || 1;
    // Against the attack's *own* length, not against a constant. `noteAttack`
    // is told the node's real duration and `swingPose` already honours it; the
    // body pose read a hardcoded 0.26 instead, so the arm and the weapon it is
    // holding animated on two different clocks. At `attackDur = 0.08` this
    // started the arm 69% through its arc — the wind-up never played — and at
    // 1.2s it reached -3.6 against a [0,1] domain, swinging the arm a full
    // turn backwards through the torso before the attack began.
    //
    // Computed once and shared with the weapon below, rather than twice. The
    // two used to be separate expressions of the same arithmetic sitting thirty
    // lines apart, which is the shape a desync grows in — and #90's remap has
    // to reach both or it *is* that desync.
    const swingT = anticipate(
      e.attackT > 0 ? Math.max(0, Math.min(1, 1 - e.attackT / e.attackDur)) : 0,
      e.attackWindup,
      e.attackDur,
    );
    scratchPose.attack01 = swingT;
    scratchPose.aim = e.aim;
    // The crouch is read off the collider rather than being told to us (#98).
    //
    // `movement.ts` is the only writer of `crouchDrop`, and it is non-zero for
    // exactly the substeps the box is lowered. So the folded silhouette and the
    // lowered AABB are the *same fact* rather than two facts kept in agreement,
    // and there is no state in which the body has ducked and the drawing has
    // not. A `noteCrouch` setter beside `noteAim` was the alternative and it is
    // the shape that desyncs: it would have to be called from the one frame the
    // crouch begins on, from the renderer's clock, against a collider that
    // changes on the simulation's.
    scratchPose.crouch = !dead && w.crouchDrop[i] > 0;
    // The landing squash, likewise. A corpse dropped down a shaft would
    // otherwise flatten and rebound under its own mask; and a body that has
    // already collapsed has nothing left to squash.
    scratchPose.squashX = dead ? 1 : w.scaleX[i];
    scratchPose.squashY = dead ? 1 : w.scaleY[i];

    // Castlevania rule: the weapon APPEARS for the swing and vanishes after.
    // Holding it permanently makes every character a statue with a stick, and
    // erases the moment the attack actually happens.
    const attacking = e.attackT > 0 && e.visual !== null && e.visual.style !== 'none';
    e.swinging = false;
    if (attacking) {
      const sp = swingPose(e.visual!.style, swingT);
      e.swinging = sp.visible;
      e.swingAngle = sp.angle;
      e.swingExtend = sp.extend;
      e.swingCurl = sp.curl;
      // Height fractions → body-local units, once, here. `swingPose` is written
      // against no particular skeleton on purpose — the same `halo` has to
      // orbit a 15-unit quadruped and a 32-unit tower — so the plan's height is
      // the only thing it is missing and this is where that is known.
      e.swingBodyPivot = sp.bodyPivot;
      if (sp.bodyPivot) {
        const h = e.body.plan.height;
        e.swingPivotX = sp.pivotX * h;
        // Folded with the body when it is crouched, by the same transform
        // `poseSkeleton` applies to `handY` — a body-pivoted style (halo, nova,
        // rotary) orbits a *height fraction of the figure*, and a figure that
        // has squatted is not the height it was. Left unfolded, the three
        // body-pivoted styles would spin around a point above a crouched
        // player's head.
        const c = scratchPose.crouch ? crouchPose(e.body.plan) : null;
        e.swingPivotY = c === null ? sp.pivotY * h : sp.pivotY * h * c.fold + c.lift;
      }
    }
    // The body raster never carries the weapon; these stay zeroed.
    scratchPose.weaponLen = 0;
    scratchPose.weaponCurl = 0;
    scratchPose.weaponSegments = 1;

    // Quantise the signature to the pixel grid: re-rasterising for sub-pixel
    // pose changes produces an identical image at real cost.
    const sig = poseSignature(scratchPose, e.body.phase, e.swinging);

    if (sig === e.sig && !e.body.dirty) return;
    e.sig = sig;

    // `standingHalfH`, not `halfH`. This is the line #98 is about: `bodyQuad`'s
    // scale multiplies bone *thicknesses* as well as bone lengths, so passing a
    // crouched `halfH` here would not squat the figure — it would shrink a
    // still-standing one to 57% and delete 70% of its texels. The crouch is
    // expressed by `scratchPose.crouch` above, which folds the joints and
    // leaves the radii, and the scale stays the constant it has always been.
    //
    // `rasterScale`, not `scale` (#147): this value only ever feeds the raster
    // buffer — `collapseSkeleton`'s extent estimate and `rasterize` itself —
    // and both need texel space, not world space, once `RENDER_K` is not 1.
    const { rasterScale } = bodyQuad(standingHalfH(w, i), e.body.plan);
    poseSkeleton(e.body.plan, scratchPose, this.skeleton);
    // The collapse is applied to the *resolved* skeleton, between posing and
    // rasterising, and not as a field on `PoseInput`. Two reasons, and both are
    // about not paying for it when nobody is dying: `poseSkeleton` is on the
    // hot path for every body in the room, and the re-raster signature has no
    // room for a finely-quantised continuous field — `PoseInput.crouch`'s own
    // doc comment does that arithmetic and lands on 130 buckets against the
    // ×49 of headroom the pack has left. `body.dirty`, which `stepDeath` sets
    // on every tick the fold moves, is what forces the re-raster instead.
    if (e.dying !== null) {
      collapseSkeleton(this.skeleton, e.dying.fold, e.dying.widen, rasterScale);
    }
    rasterize(e.body, this.skeleton, rasterScale);
    // Cache the hand joint for the world-space weapon pass.
    e.handX = this.skeleton.handX;
    e.handY = this.skeleton.handY;
  }

  /**
   * Damage the body at a world-space impact point, converting the removed
   * pixels into actual particles.
   *
   * This is the payoff of the whole system: a wound is matter leaving a body
   * and entering the world, where it lands, pools, conducts and burns like any
   * other matter. Blood that sprays is blood that stains.
   */
  wound(
    w: World,
    i: number,
    hx: number,
    hy: number,
    severity: number,
    particles: Particles,
    dirX: number,
    dirY: number,
  ): number {
    const e = this.entries.get(i);
    if (!e) return 0;

    // World -> body-local raster pixels.
    //
    // A translation and a scale. Before #147 the scale was always 1: one
    // raster texel was one world unit by construction, so the only question
    // was where the box's origin sits in the world, and that is
    // `BODY_ORIGIN_X_UNITS/Y_UNITS`: the body's centre line, at its feet.
    // `drawBodies` places the same origin the same way via
    // `bodyQuad().feetOffset` — the two agree by construction, and the geometry
    // test checks them against each other rather than against a restatement.
    // `RENDER_K` is the texel-per-world-unit scale now, and it multiplies the
    // world-space offset going in and divides the texel offset coming back
    // out, which is exactly what makes it 1 and a no-op at the shipped default.
    //
    // What was here mapped through the *collision* box (`halfW * 2` by
    // `halfH * 2`) stretched onto the raster box, and ignored the feet offset
    // entirely — after computing the correct `scale` and discarding it with
    // `void scale`. 60 of 71 bodies took their damage somewhere other than
    // where they were hit, the worst by 10 pixels, and the matter they lost
    // sprayed from that wrong place into the world.
    //
    // The half-pixel puts the continuous coordinate on the centre of the cell
    // the world point falls in, so `woundBody`'s circle test is symmetric about
    // the impact rather than biased half a pixel up and left.
    const lx = (hx - w.x[i] + BODY_ORIGIN_X_UNITS) * RENDER_K - 0.5;
    const ly = (hy - (w.y[i] + w.halfH[i]) + BODY_ORIGIN_Y_UNITS) * RENDER_K - 0.5;

    // Radius and pixel budget are both raster-texel quantities, so both carry
    // `RENDER_K`: a wound's *world-space* footprint (and the fraction of the
    // body's area it removes) is what combat balance is tuned against, and
    // that has to stay off `RENDER_K` the same way `scale` does above — a
    // fixed texel radius would shrink every wound in world terms as `RENDER_K`
    // grows, and a fixed pixel budget would remove a shrinking fraction of a
    // body whose texel count is growing by K². Squared on the budget for
    // exactly that reason: area, not length.
    const radius = (1.6 + severity * 3.2) * RENDER_K;
    const maxPixels = Math.ceil((2 + severity * 10) * RENDER_K * RENDER_K);

    return woundBody(e.body, lx, ly, radius, maxPixels, (px, py, mat) => {
      // Back to world space, then thrown along the hit direction.
      const wx = w.x[i] - BODY_ORIGIN_X_UNITS + (px + 0.5) / RENDER_K;
      const wy = w.y[i] + w.halfH[i] - BODY_ORIGIN_Y_UNITS + (py + 0.5) / RENDER_K;
      particles.emit(
        wx, wy, mat, 1,
        60 + severity * 90, 0.6,
        dirX, dirY - 0.35,
      );
    });
  }

  /**
   * Let the world write on bodies: burning chars tissue toward ash, freezing
   * toward ice. Throttled hard — this is a lasting change, and it should look
   * like an accumulating consequence rather than a flicker.
   */
  applyEnvironment(w: World, cells: CellWorld, tick: number): void {
    if (tick % 30 !== 0) return;
    for (const [i, e] of this.entries) {
      if (w.alive[i] !== 1) continue;
      const flags = cells.getFlags(Math.round(w.x[i]), Math.round(w.y[i]));
      const burning = (flags & Flag.BURNING) !== 0;
      const frozen = (flags & Flag.FROZEN) !== 0;
      if (burning || frozen) scorchBody(e.body, burning, frozen);
    }
  }

  /**
   * How far into its wind-up a body is, 0 when it is not attacking.
   *
   * A tell is a *schedule*, not a flag. `noteAttack` is handed `windup +
   * active`, so this ramps from a floor at the moment of commitment to full at
   * the end of the wind-up, and then cuts to nothing the instant the hitbox
   * appears — which is the only shape that teaches anything. A light that stays
   * on for the whole attack tells you an attack is happening, which you can
   * already see; a light that goes out *as the blow lands* tells you when to
   * move, and by going out it also marks the recovery you can punish.
   *
   * `TELL_WINDUP` is where the wind-up ends as a fraction of the announced
   * duration. The context announces only the sum (`behaviors.ts:384`), so this
   * is an assumption, and a wrong one degrades gracefully: the tell finishes
   * slightly early or slightly late, it does not invert. The floor exists
   * because a tell that starts at zero is invisible on the frame the enemy
   * commits, which is the frame it most needs to be seen.
   */
  tellOf(i: number): number {
    const e = this.entries.get(i);
    if (!e || e.attackT <= 0) return 0;
    const t = 1 - e.attackT / e.attackDur;
    if (t >= TELL_WINDUP) return 0;
    return TELL_FLOOR + (1 - TELL_FLOOR) * (t / TELL_WINDUP);
  }

  /**
   * What a body should look like this frame, beyond its own materials.
   *
   * Returns a shared object — read it before the next call, the way
   * `swingPose` is read.
   *
   * This exists because the AI's state was invisible. An enemy that has noticed
   * you, an enemy winding up, and an enemy in its recovery frames were the same
   * pixels, so the only readable event in a fight was the hit itself — which is
   * exactly the failure docs/05 §7 means by asking that a death be traceable.
   * The states are drawn, not narrated:
   *
   *   ALERT     a lift, held briefly — it has seen you
   *   COMMIT    hot and glowing on the wind-up ramp, out when the blow lands
   *   RECOVER   dimmed — this is the window you punish, and it is visible
   *   FLEE      dimmed and cooled — it has stopped being a threat
   *
   * Driven from `w.aiState` rather than from a flag this system owns, because a
   * second copy of "is it attacking" is a second thing to keep in step; the AI
   * already has the answer and it is the authority on it.
   */
  telegraph(w: World, i: number): BodyTell {
    const t = this.tell;
    const e = this.entries.get(i);
    t.r = 1;
    t.g = 1;
    t.b = 1;
    t.glow = e ? this.glowOf(e) : 0;
    if (!e) return t;

    switch (w.aiState[i]) {
      case AiState.ALERT:
        t.r = 1.14;
        t.g = 1.10;
        t.b = 1.04;
        break;
      case AiState.COMMIT: {
        const ramp = this.tellOf(i);
        t.r = 1 + ramp * 0.85;
        t.g = 1 + ramp * 0.28;
        t.b = 1 - ramp * 0.18;
        t.glow += ramp * 0.5;
        break;
      }
      case AiState.RECOVER:
        t.r = 0.78;
        t.g = 0.78;
        t.b = 0.82;
        break;
      case AiState.FLEE:
        t.r = 0.82;
        t.g = 0.86;
        t.b = 0.94;
        break;
      default:
        break;
    }
    return t;
  }

  /** Emissive contribution from a body's own materials, for the glow term. */
  glowOf(e: Entry): number {
    let g = 0;
    for (let k = 0; k < 3; k++) {
      const m = e.body.tissueMat[k];
      if (m !== VOID) g = Math.max(g, M.emissive[m]);
    }
    return g * 0.35;
  }

  get count(): number {
    return this.entries.size;
  }

  clear(): void {
    this.entries.clear();
    this.dyingIdx.length = 0;
    this.freeSlots.length = 0;
    for (let i = MAX_BODIES - 1; i >= 0; i--) this.freeSlots.push(i);
  }

  /** Iterate live entries for rendering. */
  each(fn: (i: number, e: Entry) => void): void {
    for (const [i, e] of this.entries) fn(i, e);
  }
}

/**
 * Pick a body plan from an enemy's family and proportions.
 *
 * Family is tested **first**, and that ordering is the whole content of this
 * function. A family is a statement about what an enemy *is*; the hitbox is a
 * consequence of how big it is. Tested last, `family` was unreachable: every
 * swarm def in the roster is squat enough (`halfW / halfH >= 0.67`) that one of
 * the ratio tests above always caught it, so `floater` — the plan that exists
 * to make spectres and drones hover — was never once produced from the enemy
 * roster, and the four swarms that should have hovered rendered as whatever the
 * ratio arithmetic happened to land on. A branch no input can reach is not a
 * default, it is dead code that looks like a decision.
 *
 * Silently dead is the part that matters. `planFor` falls back to `humanoid`
 * for an unknown name rather than throwing, so nothing anywhere in this system
 * raises its voice when a plan stops being used — which is why the test asserts
 * reachability from the real roster rather than trusting the branch list.
 *
 * Four enemies move as a result: silt_crawler and drone_swarm from `blob`,
 * soot_gremlin and cinder_wretch from `quadruped`. That is the fix, not a side
 * effect of it.
 *
 * `Family` is imported rather than written as `3` because a copy of an id is
 * how the id drift in docs/13 §2 started every time.
 */
export function planNameFor(family: number, halfW: number, halfH: number): string {
  if (family === Family.SWARM) return 'floater';
  const ratio = halfW / Math.max(1, halfH);
  if (halfH >= 18) return 'tower';
  if (ratio > 0.85) return 'blob';
  if (ratio > 0.62) return 'quadruped';
  return 'humanoid';
}

export { Comp };
